import { getAwsErrorMessage } from "./error-handler-aws"

// Error codes that carry a dynamic detail appended by the backend after "::"
// (see HttpException.DETAIL_SEPARATOR): FICHE_CONFIGUREE_REJECTED_BY_CSTB::<cstbDetail>
// and IMPORT_FICHE_CONFIGUREE_TO_CSTB_FAILED::<fileName>[::<cstbDetail>]
const FICHE_REJECTED_BY_CSTB_PREFIX = "FICHE_CONFIGUREE_REJECTED_BY_CSTB::"
const FICHE_IMPORT_TO_CSTB_FAILED_PREFIX = "IMPORT_FICHE_CONFIGUREE_TO_CSTB_FAILED::"
// ORGANIZATION_IS_BLOCKED::<REASON,REASON>|<organization name>
const ORGANIZATION_IS_BLOCKED_PREFIX = "ORGANIZATION_IS_BLOCKED::"
// LIBRARY_REQUEST_INVALID::<why> — a request of the draft of the next library version (admin page, US 2552)
const LIBRARY_REQUEST_INVALID_PREFIX = "LIBRARY_REQUEST_INVALID::"

export function getOrganizationBlockedMessage(detail: string): string {
  const separatorIndex = detail.indexOf("|")
  const reasons = (separatorIndex >= 0 ? detail.substring(0, separatorIndex) : "").split(",").filter(Boolean)
  const name = separatorIndex >= 0 ? detail.substring(separatorIndex + 1).trim() : ""
  const organization = name ? `Votre organisation « ${name} »` : "Votre organisation"

  const subscriptionEnded = reasons.includes("SUBSCRIPTION_ENDED")
  const paymentLate = reasons.includes("PAYMENT_LATE")
  let why = ""
  if (subscriptionEnded && paymentLate) {
    why = " : la date limite de son abonnement est dépassée et un retard de paiement a été constaté"
  } else if (subscriptionEnded) {
    why = " : la date limite de son abonnement est dépassée"
  } else if (paymentLate) {
    why = " en raison d'un retard de paiement"
  }
  return `${organization} est bloquée${why}. Contactez le support Time To Beem pour rétablir l'accès.`
}

/** Returns the stable backend error code, excluding any dynamic {@code ::detail} suffix. */
export function getErrorCode(error: unknown): string | undefined {
  if (!(error instanceof Error)) {
    return undefined
  }
  return error.message.split("::", 1)[0]
}

export function getFicheRejectedByCstbMessage(detail: string): string {
  return `La fiche configurée a été refusée par le moteur réglementaire : ${detail}`
}

export function getFicheImportToCstbFailedMessage(fileNameAndDetail: string): string {
  const separatorIndex = fileNameAndDetail.indexOf("::")
  if (separatorIndex > 0) {
    const fileName = fileNameAndDetail.substring(0, separatorIndex)
    const detail = fileNameAndDetail.substring(separatorIndex + 2)
    return `La fiche configurée « ${fileName} » a été refusée par le moteur réglementaire : ${detail}`
  }
  return `La fiche configurée « ${fileNameAndDetail} » a été refusée par le moteur réglementaire`
}

export function getErrorMessage(error: Error): string {
  let message = error.message
  if (error.name !== "Error") {
    message = error.name
    return getAwsErrorMessage(message)
  }
  if (message?.startsWith("Error:")) {
    message = message.substring(6, message.length).trim()
  }
  if (message?.startsWith(FICHE_REJECTED_BY_CSTB_PREFIX)) {
    return getFicheRejectedByCstbMessage(message.substring(FICHE_REJECTED_BY_CSTB_PREFIX.length))
  }
  if (message?.startsWith(FICHE_IMPORT_TO_CSTB_FAILED_PREFIX)) {
    return getFicheImportToCstbFailedMessage(message.substring(FICHE_IMPORT_TO_CSTB_FAILED_PREFIX.length))
  }
  if (message?.startsWith(ORGANIZATION_IS_BLOCKED_PREFIX)) {
    return getOrganizationBlockedMessage(message.substring(ORGANIZATION_IS_BLOCKED_PREFIX.length))
  }
  if (message?.startsWith(LIBRARY_REQUEST_INVALID_PREFIX)) {
    return `Demande refusée : ${message.substring(LIBRARY_REQUEST_INVALID_PREFIX.length)}`
  }
  switch (message) {
    case "401_FORBIDDEN":
    case "403_FORBIDDEN":
      return "Vous n'avez pas le droit d'accéder à la ressource"
    case "REQUEST_HEADER_TOO_LARGE":
      return (
        "Le serveur a refusé la requête : les cookies enregistrés par votre navigateur pour ce site sont trop " +
        "volumineux (anciennes sessions jamais nettoyées). Ils viennent d'être supprimés : veuillez vous reconnecter. " +
        "Si le problème persiste, supprimez manuellement les cookies du site dans votre navigateur."
      )
    // Never shown by useHttp: the page reloads in the new version or its banner asks the user to (front-update.ts)
    case "FRONT_VERSION_OUTDATED":
      return "Time To Beem a été mis à jour : rechargez la page pour continuer."
    case "INVITATION_NOT_VALID_ANYMORE":
      return "L'invitation n'est plus valide"
    case "ORGANIZATION_LICENCE_LIMIT_REACHED":
      return "Il n'y a plus de licence disponible pour votre organisation. Veuillez nous contacter pour ajouter des licences"
    case "GAMME_NOT_FOUND":
      return "La gamme n'a pas été trouvée"
    case "GAMME_FORBIDDEN":
      return "Vous n'avez pas accès à cette gamme"
    case "GAMME_TEAM_MEMBER_NOT_IN_ORGANIZATION":
      return "Ce collaborateur n'appartient pas à votre organisation"
    case "NOTIFICATION_NOT_FOUND":
      return "Cette notification n'existe plus"
    case "MCP_OPTION_MISSING":
      return "Votre organisation n'a pas l'option « Connecteur IA ». Contactez le support Time To Beem pour l'activer."
    case "MCP_ACCESS_TOKEN_NOT_FOUND":
      return "Ce jeton d'accès n'existe plus ou a déjà été révoqué."
    case "MCP_ACCESS_TOKEN_NAME_INVALID":
      return "Le nom du jeton doit contenir entre 1 et 60 caractères."
    case "MCP_ACCESS_TOKEN_VALIDITY_INVALID":
      return "La durée de validité d'un jeton va d'un jour à un an."
    case "MCP_ACCESS_TOKEN_LIMIT_REACHED":
      return "Vous avez déjà 10 jetons d'accès en cours : révoquez-en un pour en créer un nouveau."
    case "MCP_OAUTH_CONNECTION_NOT_FOUND":
      return "Cette application n'est plus connectée à votre compte."
    case "MCP_OAUTH_CONSENT_NOT_FOUND":
      return "Cette demande de connexion a expiré ou a déjà reçu une réponse. Relancez la connexion depuis votre agent IA."
    case "AGENCY_NOT_FOUND":
      return "Cette agence n’est plus disponible dans votre organisation."
    case "AGENCY_NAME_INVALID":
      return "Le nom de l’agence doit contenir entre 1 et 256 caractères."
    case "AGENCY_NAME_ALREADY_EXISTS":
      return "Une agence porte déjà ce nom dans cette organisation."
    case "AGENCY_HAS_PROJECTS":
      return "Cette agence porte des projets : rattachez-les à une autre agence avant de la supprimer."
    case "AGENCY_HAS_USERS":
      return "Retirez les rattachements des collaborateurs avant de supprimer cette agence."
    case "AGENCY_TARGET_INVALID":
      return "L’objectif carbone doit être un nombre positif ou nul."
    case "REGION_NOT_FOUND":
      return "Cette région n’est plus disponible dans votre organisation."
    case "REGION_NAME_INVALID":
      return "Le nom de la région doit contenir entre 1 et 256 caractères."
    case "REGION_NAME_ALREADY_EXISTS":
      return "Cette région existe déjà dans cette organisation."
    case "REGION_HAS_AGENCIES":
      return "Retirez les agences avant de supprimer cette région."
    case "ORGANIZATION_NOT_FOUND":
      return "L'organisation n'a pas été trouvée"
    case "EMAIL_ERROR":
      return "Erreur lors de l'envoi de l'e-mail"
    case "EMAIL_IS_MANDATORY":
      return "Il est obligatoire de spécifier un email"
    case "ORGANIZATION_NOT_INVITED":
      return "L'utilisateur appartient déjà à une organisation qui n'est pas invitée"
    case "ORGANIZATION_ID_SHOULD_NOT_BE_EMPTY":
      return "L'ID de l'organisation est vide"
    case "LIMIT_TOO_HIGH":
      return "La limite ne doit pas être supérieure à 100"
    case "FORBIDDEN":
      return "L'utilisateur n'a pas assez de droits sur le projet"
    case "INCORRECT_OBJECTID":
      return "Le format de l'id est incorrect"
    case "USER_NOT_IN_PROJECT":
      return "L'utilisateur ne fait pas partie du projet"
    case "NOT_ARCHITECT":
      return "L'utilisateur doit être ARCHITECTE sur le projet"
    case "NO_MODEL_FOR_PROJECT_AND_PHASE":
      return "Pas de modèle pour ce projet et cette phase"
    case "USER_NOT_FOUND":
      return "Cet utilisateur est introuvable"
    case "NOT_AUTHORIZED":
      return "Vous n'avez pas le droit d'accéder à la ressource"
    case "USER_WITH_EMAIL_ALREADY_EXIST":
      return "Un utilisateur avec cet email existe déjà"
    case "USER_ALREADY_CONFIRMED":
      return "Ce compte est déjà confirmé, aucun code à renvoyer"
    case "USER_NOT_IN_ORGANIZATION":
      return "Votre utilisateur ne fait pas partie de la bonne organisation"
    case "USER_NOT_IN_SAME_ORGANIZATION":
      return "Votre utilisateur ne fait pas partie de la même organisation"
    case "ORGANIZATION_NOT_ACTIVE":
      return "Votre organisation est désactivée"
    case "USER_ALREADY_DELETED":
      return "Cet utilisateur est déjà supprimé"
    case "INVALID_PASSWORD":
      return "Password invalide"
    case "USER_ALREADY_EXISTS":
      return "Cet utilisateur existe déjà"
    case "INCORRECT_PHONE_NUMBER":
      return "Le téléphone est incorrect"
    case "UNIT_NOT_SUPPORTED":
      return "L'unité n'est pas supportée"
    case "CODE_ACV_CUSTOM_NOT_FOUND":
      return "La donnée custom est introuvable"
    case "NO_QUANTITY_FOR_UNIT":
      return "Pas de quantité pour cette unité"
    case "UNKNOWN_UNIT":
      return "Unité inconnue"
    case "NO_QUANTITIES":
      return "Pas de quantité"
    case "NO_CUSTOM_ACV_ID_TO_UPDATE":
      return "Pas de donnée ACV personnalisée à mettre à jour"
    case "NO_CALCULATION_ID_TO_UPDATE":
      return "Le calcul à mettre à jour n'existe pas"
    case "QUANTITY_IS_EMPTY_FOR_UNIT":
      return "La quantité est vide pour cette unité"
    case "UNKNOWN_UNIT_ERROR":
      return "L'unité est inconnue"
    case "UNKNOWN_VARIANT":
      return "L'étude n'existe pas"
    case "NOT_BE":
      return "Vous devez avoir le rôle Bureau d'Etude"
    case "NOT_MOA":
      return "Vous devez avoir le rôle MOA"
    case "NOT_ADMINISTRATOR":
      return "Vous devez avoir le rôle administrateur"
    case "NOT_CONSULTANT":
      return "Vous devez avoir le rôle consultant"
    case "NOT_CORRECT_ROLE":
      return "Vous n'avez pas le correct rôle"
    case "MANDATORY_INIES_RECORD_NOT_FOUND":
      return "La fiche INIES n'a pas été trouvée"
    case "CALCULATION_NOT_FOUND":
      return "Il n'y a pas de données de calcul sur ce projet pour cette phase"
    case "EXTRACTED_CODE_DOES_NOT_EXIST":
      return "Le code extrait n'existe pas"
    case "EXTRACTED_CODE_WRONG_PROJECT":
      return "Le code extrait n'appartient pas à ce projet"
    case "VARIANT_DOES_NOT_EXIST":
      return "Une des 2 études n'existe pas"
    case "VARIANT_NOT_FOUND":
      return "Pas d'étude trouvée sur ce projet"
    case "MANDATORY_MATERIAL_ID_NOT_FOUND":
      return "Le matériau est introuvable alors qu'il est obligatoire"
    case "ADDITIONAL_CODE_NOT_FOUND":
      return "La donnée supplémentaire n'existe pas"
    case "VARIANT_ID_MISSMATCH_PROJECT_ID":
      return "L'étude n'appartient pas au projet actuellement sélectionné"
    case "USER_NOT_AN_ARCHITECT":
      return "L'utilisateur n'est pas une architecte"
    case "USER_NOT_ACTIVE_ON_PROJECT":
      return "Vous n'êtes pas affecté sur ce projet"
    case "INVALID_PHASE":
      return "La phase n'est pas valide"
    case "UNKNOWN_BIM_MODEL_ID":
      return "Il manque l'identifiant de la maquette BIM"
    case "NO_TUTORIAL_MODEL":
      return "Impossible de savoir s'il faut afficher le tutoriel"
    case "TUTORIAL_VIDEO_NOT_FOUND":
      return "La vidéo de tutoriel est introuvable"
    case "TUTORIAL_VIDEO_FILE_INCORRECT":
      return "Le fichier doit être une vidéo (mp4, webm…)"
    case "NO_PROJECT_ON_MODEL":
      return "Il n'y a pas de projet associé à la maquette numérique de votre requête"
    case "CODE_DOES_NOT_EXISTS_ON_PROJECT":
      return "Le code n'appartient pas au projet sélectionné"
    case "PROJECT_NOT_FOUND":
      return "Le projet est introuvable"
    case "RESULT_NOT_FOUND":
      return "Pas de résultat pour ce calcul"
    case "PROJECT_SHOULD_HAVE_AN_ID":
      return "Le projet doit avoir un id"
    case "PROJECT_NAME_ALREADY_EXIST":
      return "Ce nom de projet existe déjà"
    case "MAX_PROJECT_NAME":
      return "Pas plus de 100 projets à partir du même projet ne peuvent être créés"
    case "USER_ORGANIZATION_DOES_NOT_EXISTS":
      return "L'organisation de votre utilisateur n'existe pas"
    case "ORGANIZATION_MAX_PROJECT_COUNT_REACHED":
      return "Vous avez atteint le maximum de projet autorisé"
    case "DELETE_NOT_IMPLEMENTED":
      return "Il est impossible de supprimer cette donnée"
    case "INIES_SYNCHRONIZATION_ALREADY_RUNNING":
      return "Une synchronisation INIES est déjà en cours"
    case "INIES_CHANGES_NOT_ENABLED":
      return "Les modifications INIES ne font pas partie de l'offre de votre organisation"
    case "NO_ROOT_NOMENCLATURE":
      return "Il n'y a pas de nomenclature"
    case "NO_COGNITO_RIGHTS":
      return "Vous n'avez pas le droit d'effectuer cette action"
    case "USER_DOES_NOT_EXISTS":
      return "L'utilisateur n'existe pas"
    case "NO_DELETE":
      return "Impossible de supprimer cette donnée"
    case "COGNITO_UNKNOWN_ERROR":
      return "Erreur inconnue de la part du serveur d'authentification"
    case "INVALID_PHONE_NUMBER_FORMAT":
      return "Le format du numéro de téléphone est invalide"
    case "INVALID_PARAMETER":
      return "Vous avez envoyé un paramètre invalide"
    case "INVALID_PASSWORD_EXCEPTION":
      return "Le mot de passe est invalide"
    case "INVALID_PARAMETER_EXCEPTION":
      return "Vous avez envoyé un paramètre invalide"
    case "UNKNOWN_USER_ERROR":
      return "L'utilisateur est inconnu"
    case "USER_ORGANIZATION_NOT_FOUND":
      return "L'organisation de l'utilisateur n'a pas été trouvée"
    case "DOMAIN_NAME_FORBIDDEN":
      return "Votre adresse email doit être une adresse d'entreprise"
    case "DOMAIN_LIST_IS_LOADING":
      return "La liste des noms de domaine autorisés pour l'email n'a pas fini de charger. Veuillez ré-essayer dans quelques secondes"
    case "MAX_RSEE_PROJECT_REACHED":
      return "Vous avez atteint le maximum de RSEE que vous pouvez charger"
    case "INVITATION_DOES_NOT_EXIST":
      return "L'invitation que vous souhaitez utiliser n'existe pas"
    case "INVITATION_PENDING_IN_ANOTHER_ORGANIZATION":
      return "Cette adresse a déjà une invitation en attente dans une autre organisation : elle doit y être annulée avant de pouvoir l'inviter ici"
    case "NO_FILE":
      return "Pas de fichier trouvé"
    case "COMPLETION_WRONG_VARIANT":
      return "Erreur lors de la modification du code complétion, il appartient à une autre étude"
    case "ERROR_CREATING_SIGNED_REQUEST":
      return "Impossible de charger les vidéos"
    case "DVE_CANNOT_BE_0":
      return "L'unité de la fiche INIES est incorrecte"
    case "ADDITIONAL_CODE_WRONG_VARIANT":
      return "Le code additionnel n'appartient pas à l'étude envoyée"
    case "CODE_COMPLETION_DOES_NOT_EXISTS":
      return "Le code completion n'existe pas"
    case "VARIANT_WRONG_PROJECT":
      return "L'étude n'appartient pas à ce projet"
    case "ID_NEEDED_FOR_UPDATE":
      return "L'id de l'objet est obligatoire pour une mise à jour"
    case "VARIANT_DOES_NOT_EXISTS":
      return "L'étude demandée n'existe pas"
    case "CODE_ACV_CUSTOM_ALREADY_EXISTS":
      return "Il ne peut pas y avoir 2 codes avec le même nom"
    case "NO_INVITATION_TO_SELF":
      return "Vous ne pouvez pas envoyer d'invitation à vous même"
    case "BOTH_MUST_BE_NULL_OR_NOT_NULL":
      return "La requête pour récupérer les projets suivants est incorrecte"
    case "CODE_COMPLETION_INCORRECT_ID":
      return "L'id du code est incorrect"
    case "WRONG_RSEE_FILE":
      return "Soit le fichier n'est pas un RSEE valide, soit son format est invalide. Formats supportés: .xml, .zip"
    case "NO_RSEE_FOUND":
      return "Ce fichier n'est pas un RSEE : aucune donnée RE2020 n'y a été trouvée (ni partie environnementale, ni étude énergie). Déposez l'export RSEE ou RSET de votre logiciel (.xml ou .zip)."
    case "CHECKLIST_OVERRIDE_NOT_FOUND":
      return "Cette décision n'existe plus : rechargez la page"
    case "GAMME_ASSIGNMENT_NOT_FOUND":
      return "Ce choix de gammes n'existe plus : rechargez la page"
    case "REFERENCE_SELECTION_NOT_FOUND":
      return "Ce choix de documents de référence n'existe plus : rechargez la page"
    case "ENDPOINT_DOES_NOT_EXISTS":
      return "Le endpoint backend n'existe pas"
    case "NOT_ON_PROJECT":
      return "Vous n'avez pas le droit de voir ce RSEE"
    case "NO_CODE_EXTRAIT":
      return "Aucun code n'est affecté dans la maquette. La maquette est ignorée"
    case "FORBIDDEN_FILE_SIZE":
      return "Le fichier est trop volumineux. La taille maximale est de 3 Mo"
    case "FORBIDDEN_FILE_DIMENSIONS":
      return "Le fichier est trop volumineux. La taille maximale est de 1920 x 1080"
    case "FORBIDDEN_FILE_EXTENSIONS":
      return "Le format du fichier n'est pas valide. Voici les extensions acceptées: '.png, .jpeg, .jpg'"
    case "FORBIDDEN_FICHE_CONFIGUREE_EXTENSIONS":
      return "Le format du fichier n'est pas valide. Voici les extensions acceptées: '.zip, .xml'"
    case "FILE_NEEDS_NAME":
      return "Erreur lors de la récupération du nom du fichier"
    case "FILE_INCORRECT":
      return "Le fichier n'a pas pu être lu. Veuillez ré-essayer"
    case "ZIP_NOT_SUPPORTED":
      return "Les fichiers zip ne sont pas encore supportés pour les fiches configurées. Veuillez poster l'XML"
    case "FICHE_CONFIGUREE_PUBLIC_ADDRESS_MISSING":
      return (
        "La fiche configurée n'a pas d'adresse publique (balise « PublicAddress » vide dans le XML). " +
        "Le moteur réglementaire refuserait cette fiche au moment du calcul. Demandez à l'éditeur du configurateur un export avec l'adresse publique renseignée"
      )
    case "FICHE_CONFIGUREE_REJECTED_BY_CSTB":
      return "La fiche configurée a été refusée par le moteur réglementaire"
    case "FICHE_CONFIGUREE_CSTB_UNREACHABLE":
      return "Impossible de vérifier la fiche configurée : le moteur réglementaire est injoignable. Veuillez ré-essayer plus tard"
    case "USER_NOT_ON_PROJECT_RSEE":
      return "Vous n'avez pas le droit d'accéder à ce projet"
    case "ORGANIZATION_MAX_ARCHIVED_PROJECT_COUNT_REACHED":
      return "Votre limite de projets archivés a été atteinte, veuillez contacter le support client ou aller consulter la page Mon offre"
    case "ORGANIZATION_MAX_UNARCHIVE_CREDIT_COUNT_REACHED":
      return "Votre limite de crédits de désarchivage a été atteinte, veuillez contacter le support client ou aller consulter la page Mon offre"
    case "PROJECT_IS_ARCHIVED":
      return "Le projet est à l'état archivé, l'action n'est pas possible"
    case "DOCUMENT_NOT_FOUND":
      return "Le RSEE n'existe pas"
    case "SHARED_DOCUMENT_DELETED":
      return "Ce lien pointe vers un document qui a été supprimé"
    case "SHARED_BS_VARIANT_DELETED":
      return "Ce lien pointe vers une variante qui a été supprimée"
    case "SHARED_BS_TAB_NOT_AVAILABLE":
      return "Un des onglets choisis n'existe pas pour ce type de variante"
    case "SHARED_BS_VIEW_NOT_FOUND":
      return "La vue de ce bâtiment n'est pas disponible"
    case "INVALID_SHARED_BS_VIEW":
      return "Une des vues des bâtiments n'a pas pu être enregistrée : reprenez la vue et générez le lien à nouveau"
    case "NO_SHARE_LINK_FOUND":
      return "Ce lien de partage n'existe pas ou a été révoqué"
    case "RSEE_DOCUMENT_DOES_NOT_EXISTS":
      return "Le document RSEE n'existe plus. Veuillez patienter pendant le rafraîchissement des données"
    case "INCORRECT_XML":
    case "INCORRECT_FICHE_XML":
      return "Le XML du RSEE est incorrect"
    case "INCORRECT_DATASCOMP_VERSION":
      return "La version de la partie données générales (Datas_Comp) est inconnue"
    case "EMPTY_DATASCOMP_VERSION":
      return "Pas de version pour la partie données générales (Datas_Comp) du RSEE"
    case "EMPTY_DATASCOMP":
      return "Aucune information générale du projet n'est renseignée (Nom, Adresse, Logiciel, BE etc...). Êtes-vous sûr qu'il s'agit d'un RSEE RE2020"
    case "INCORRECT_DATASCOMP_XML":
      return "Impossible de lire la partie données générales (Datas_Comp) du RSEE"
    case "EMPTY_RSENV_VERSION":
      return "Pas de version pour la partie RsEnv du RSEE"
    case "INCORRECT_RSET_VERSION":
      return 'La version de la partie "Rset" est inconnue'
    case "INCORRECT_RSENV_VERSION":
      return 'La version de la partie "RsEnv" est inconnue'
    case "EMPTY_RSENV":
      return 'Aucune information pour la partie "RsEnv" du RSEE'
    case "INCORRECT_RSENV_XML":
      return 'Impossible de lire la partie "RsEnv" du RSEE'
    case "UNZIP_ERROR":
      return "Erreur durant le dé-zippage du fichier. Veuillez ré-essayer"
    case "WRONG_RSEE_DATA":
      return "Les données contenues dans le RSEE sont incorrectes"
    case "NO_IMG_FOR_CODE":
      return "Aucune image n'est affectée à ce code"
    case "NO_IMG_FOR_PROJECT":
      return "Aucune image n'est affectée à ce projet"
    case "IMAGE_UNREADABLE":
      return "L'image que vous avez sélectionnée ne semble pas être une image valide"
    case "EMPTY_XML":
      return "Le fichier XML est vide"
    case "FICHE_NOT_EXISTS_ON_PROJECT":
      return "Ce matériau n'existe pas"
    case "NO_FICHE_CONFIGUREE_FOUND":
      return "Aucune fiche configurée n'a été trouvée"
    case "USER_EMAIL_WRONG_DOMAIN":
      return "Vous ne pouvez pas rattacher cet utilisateur, car le nom de domaine de son adresse email n'est pas le même que celui de votre organisation"
    case "CANNOT_DISABLE_MEGA_USER":
      return 'Un utilisateur qui est "Administrateur de l\'organisation" ne peut pas être supprimé. Changez ses droits ou appelez le support pour effectuer cette action'
    case "USER_HAS_NO_ORGANIZATION":
      return "Aucune organisation n'est attachée à votre utilisateur. Cette action n'est pas autorisée en test gratuit"
    case "INCORRECT_DASHBOARD_VERSION":
      return "La version du document est incorrecte"
    case "NO_DEFAULT_VERSION":
      return "Pas de version par défaut des tableaux de bord sur cet environnement"
    case "BS_CODE_INCORRECT":
      return "L'un des codes contenu dans la maquette est incorrect"
    case "ROLE_ABOVE_ADMINISTRATOR":
      return "Vous ne pouvez pas attribuer un rôle supérieur au vôtre"
    case "COLLABORATOR_ABOVE_ADMINISTRATOR":
      return "Vous ne pouvez pas gérer un collaborateur dont le rôle est supérieur au vôtre"
    case "COLLABORATOR_OUT_OF_ADMINISTERED_SCOPE":
      return "Ce collaborateur n'est pas dans votre périmètre : vous ne gérez que les comptes des agences dont vous êtes administrateur"
    case "AGENCY_ADMINISTRATOR_WITHOUT_AGENCY":
      return "Un administrateur d'agence doit être rattaché à une agence"
    case "REGION_ADMINISTRATOR_WITHOUT_REGION":
      return "Un administrateur de région doit être rattaché à une agence d'une région"
    case "LAST_MEGA_USER":
      return "Impossible de modifier les droits du dernier 'administrateur' de l'organisation. Il faut toujours au moins un administrateur"
    case "NO_REFERENCE":
      return "Aucun code référence n'a été trouvé pour ce code extrait"
    case "USER_EMAIL_DOMAIN_FORBIDDEN":
      return "Time To Beem est un service B2B. Merci d'utiliser une adresse email professionnelle"
    case "NO_SHARE_INFORMATION_FOUND":
      return "Le lien utilisé est invalide"
    case "MAX_USAGE_COUNT_REACHED":
      return "Vous avez atteint la limite du nombre d'utilisation de ce lien"
    case "MAX_VALIDITY_DATE_REACHED":
      return "Le lien est périmé. La date de validité a été dépassée"
    case "LINK_NOT_FOUND":
      return "Le lien n'existe pas"
    case "UUID_COLLISION":
      return "Une erreur s'est produite à la sauvegarde du lien. Veuillez ré-essayer"
    case "TOO_MANY_FILE_ZIP":
      return "Le fichier .Zip contient trop de fichiers. Veuillez ré-essayer"
    case "USER_SHOULD_BE_SAME_ORGANIZATION_HAS_PROJECT":
      return "L'utilisateur ne fait pas partie de l'organisation qui possède ce projet"
    case "NEED_AT_LEAST_ONE_USER":
      return "Il faut au moins un utilisateur dans l'équipe du projet"
    case "RSEE_INCORRECT_FILE":
      return "Ce fichier n'est pas un RSEE lisible : son contenu XML est illisible ou incorrect."
    case "RSEE_INCORRECT_FILE_IN_ZIP":
      return "Ce zip ne semble pas être un RSEE : il contient un fichier qui n'est ni un RSEE ni une fiche configurée lisible. Un zip RSEE ne contient que le fichier RSEE et ses fiches configurées (.xml)."
    case "RSEE_FICHE_CONFIGUREE_NOT_FOUND_IN_ZIP":
      return "Le fichier ZIP ne contient pas toutes les fiches configurées référencées par le RSEE."
    case "RSEE_PROJECT_NAME_ALREADY_EXIST":
      return "Ce nom de projet existe déjà"
    case "ORGANIZATION_NAME_ALREADY_EXIST":
      return "Une organisation avec ce nom existe déjà"
    case "COMPANY_SEARCH_UNAVAILABLE":
      return "L'annuaire des entreprises ne répond pas : saisissez les informations de l'organisation à la main"
    case "INVALID_DATE":
      return "La date est invalide"
    case "BS_MATERIAL_INIES_RECORD_DOES_NOT_EXISTS":
      return "Une des fiches INIES n'existe pas"
    case "FICHE_CONFIGUREE_AFFECTED_ON_OVERRIDDEN":
      return "La fiche configurée ne peut pas être supprimée car elle est affectée sur un matériau personnalisé"
    case "FICHE_CONFIGUREE_AFFECTED_ON_CUSTOM":
      return "La fiche configurée ne peut pas être supprimée car elle est affectée sur un matériau ajouté"
    case "FICHE_CONFIGUREE_AFFECTED_ON_RSEE_CUSTOM_INPUT":
      return "La fiche configurée ne peut pas être supprimée car elle est affectée sur une personnalisation Beem Pilot"
    case "FICHE_CONFIGUREE_AFFECTED_ON_CHANTIER_COMPONENT":
      return "La fiche configurée ne peut pas être supprimée car elle est affectée sur un composant chantier"
    case "FICHE_CONFIGUREE_AFFECTED_ON_PARCELLE_COMPONENT":
      return "La fiche configurée ne peut pas être supprimée car elle est affectée sur un composant parcelle"
    case "FICHE_CONFIGUREE_NO_PROJECT":
      return "La fiche configurée ne peut pas être supprimée car elle n'est rattachée à aucun projet"
    case "FICHE_CONFIGUREE_NOT_ADDED_BY_USER":
      return "Seules les fiches configurées ajoutées manuellement peuvent être supprimées"
    case "VARIANT_SAME_NAME":
      return "Les études ne doivent pas avoir le même nom"
    case "PROJECT_SOURCE_NOT_FOUND":
      return "Le projet source n'existe pas"
    case "PROJECT_TARGET_NOT_FOUND":
      return "Le projet cible n'existe pas"
    case "MATERIAL_DOES_NOT_EXISTS":
      return "Un des matériaux renseigné dans le projet source n'existe pas. Veuillez contacter le support"
    case "CODE_CUSTOM_NOT_FOUND":
      return "Une anomalie s'est produite lors de la copie. L'un des codes complétions issus du projet d'origine a une anomalie"
    case "SOURCE_CALCULATION_NOT_FOUND":
      return "Le projet sélectionné n'a pas de matériaux pour cette phase"
    case "TARGET_CALCULATION_NOT_FOUND":
      return "La partie calcul du projet cible n'est pas initialisée"
    case "IMPACT_FOR_PHASE_NUMBER_FORMAT_ERROR":
      return "L'impact sur une phase d'une fiche est dans un format de nombre inconnu (exemple: '1,21.12.1'). Cela provient probablement d'une erreur dans une fiche configurée"
    case "EMPTY_CODE_EXTRAIT_LIST":
      return "La maquette ne contient aucun code. Elle est probablement mal configurée. Veuillez consulter le support si vous ne comprenez pas pourquoi c'est le cas"
    case "BS_USAGE_NOT_FOUND":
      return "L'usage du bâtiment n'est pas répertorié"
    case "NO_BIOSOURCE_FOR_USAGE":
      return "Nous n'avons pas prévu de seuil biosourcé pour cet usage"
    case "BS_PROJECT_NOT_FOUND":
      return "Le projet Beem Shot n'existe pas"
    case "SURFACE_REF_CANNOT_BE_ZERO":
      return "La surface de référence ne doit pas être égale à zéro"
    case "BS_ITEM_NOT_FOUND":
      return "Cet élément n'existe pas"
    case "FRAG_NOT_AVAILABLE":
      // Used as a sentinel by ensureFragForBimModel to trigger lazy IFC→.frag conversion.
      // Silenced in use-http.ts.silentCodes so this message rarely surfaces — kept here so the
      // user gets something readable instead of the raw code if a future caller bypasses the
      // sentinel handling.
      return "Le modèle BIM n'a pas encore été converti au format Fragments. Conversion en cours…"
    case "ERROR_FRAG_FILE_STREAM":
      return "Impossible de récupérer le fichier Fragments depuis le stockage. Veuillez ré-essayer ou contacter le support"
    case "CODE_EXTRAIT_ESCALIER_MANDATORY":
      return "Il y a un escalier qui n'est pas relié à un élément de la maquette (pas de code extrait). Or c'est obligatoire pour connaitre la quantité"
    case "WRONG_CODE_EXTRAIT_ESCALIER":
      return "Il y a un escalier qui a un code extrait différent de SUP_ESC. C'est incorrect"
    case "PROJECT_INVALID_DATE":
      return "La date est invalide"
    case "PROJECT_STATUS_NOT_ALLOWED":
      return "Ce statut ne peut pas être appliqué au projet."
    case " NO_IMG_FOR_BS_PROJECT":
      return "Aucune image n'est affectée à ce projet Beem Shot"
    case "NOT_MAIN_USER":
      return "L'utilisateur n'est pas l'utilisateur principal du projet Beem Shot"
    case "NOT_BIM_MODEL_OWNER":
      return "L'utilisateur n'est pas le propriétaire de la maquette Bim"
    case "CODE_EXTRAIT_SHOULD_EXIST":
      return "Un item, extrait de la maquette, ne contient pas d'information extraite. Veuillez contacter le support"
    case "FLOOR_NUMBER_MUST_NOT_BE_ZERO":
      return "Le nombre de niveaux doit être au moins 1, actuellement, c'est 0"
    case "BIM_MODEL_TOKEN_NOT_FOUND":
      return "Vous n'avez pas le droit d'accéder à ce modèle. Le token fourni n'existe pas"
    case "BIM_MODEL_TOKEN_USED":
      return "Le token fourni a déjà été utilisé"
    case "SURFACE_MUST_NOT_BE_ZERO":
      return "La surface ne peut pas être égale à zéro"
    case "MACRO_COMPONENT_NOT_FOUND":
      return "Le composant n'a pas été trouvé"
    case "FILENAME_EMPTY":
      return "Impossible de générer une clé d'identification pour le fichier car le nom du fichier est vide"
    case "NAME_FIELD_TOO_LONG":
      return "Le nom dépasse la taille maximale autorisée"
    case "COMMENTS_FIELD_TOO_LONG":
      return "Le commentaire dépasse la taille maximale autorisée"
    case "VARIANT_DESCRIPTION_FIELD_TOO_LONG":
      return "La description de l'étude est trop longue"
    case "VARIANT_NAME_FIELD_TOO_LONG":
      return "Le nom de l'étude est trop long"
    case "BS_VARIANT_NOT_FOUND":
      return "L'étude n'existe pas"
    case "NOT_ICADE_USER":
      return "Votre utilisateur ne fait pas partie de l'organisation qui a accès aux projets Kairnial"
    case "NOT_MEGA_USER":
      return "Votre utilisateur n'est pas administrateur"
    case "NO_PROJECT_IN_DB":
      return "Le projet n'existe pas encore dans Time to Beem"
    case "UNAUTHORIZED_TOKEN":
      return "Le lien d'accès au tableau n'est pas valide"
    case "RSEE_DOCUMENT_NOT_AUTHORIZED":
      return "Ce document n'appartient pas à ce projet"
    // Recorded as the import error of a Kairnial document the synchronization could not import.
    case "ERROR_GETTING_FILE":
      return "Le fichier transmis par Kairnial n'a pas pu être lu"
    case "KAIRNIAL_ERROR":
      return "Kairnial a renvoyé une erreur"
    // Thrown by both the Kairnial and the INIES clients: the message names neither.
    case "AUTHENTICATION_FAILED":
      return "L'authentification auprès du service externe (Kairnial ou INIES) a été refusée"
    case "ICADE_BRANCH_NO_FOUND":
      return "L'agence de ce projet n'a pas été trouvée"
    case "SYNCHRONIZATION_ALREADY_IN_PROGRESS":
      return "La synchronisation est déjà en cours. Veuillez attendre quelques minutes qu'elle se termine. Si vos données ne sont toujours pas à jour, alors vous pourrez la relancer"
    case "NO_MODEL_FOR_VARIANT":
      return "Pas de Maquette numérique pour cette étude"
    case "BEEM_SHOT_SURFACE_PLANCHER_EMPRISE_INVALID":
      return "La surface plancher doit être plus grande ou égale à l'emprise au sol"
    case "BEEM_SHOT_EMPRISE_SURFACE_PARCELLE_INVALID":
      return "L’emprise au sol de votre bâtiment dépasse la surface de la parcelle. Merci de vérifier que les dimensions saisies pour le bâtiment et la parcelle sont correctes."
    case "USER_ID_MUST_NOT_NULL":
      return "La requête a échoué car aucun utilisateur n'est sélectionné"
    case "PROJECT_DELETED":
      return "Un projet qui est 'Supprimé' ne peut pas être modifié"
    case "NO_ICADE_IN_DB":
      return "L'organisation Icade n'existe pas"
    case "BS_MATERIAL_RESULT_NOT_FOUND":
      return "Les informations du matériau n'ont pas été trouvées"
    case "BS_COMENV_RESULT_NOT_FOUND":
      return "Aucun calcul Comenv n'a encore été effectué pour ce bâtiment. Lancez le calcul depuis le formulaire."
    case "BS_COMENV_RESULT_ALREADY_EXISTS":
      return "Ce bâtiment a déjà été calculé, son résultat se met à jour automatiquement. Rechargez la page pour le suivre."
    case "INVALID_INIES_ID":
      return "Impossible de convertir l'id de la fiche en id inies"
    case "CUSTOM_MATERIAL_ID_SHOULD_NOT_BE_NULL":
      return "L'id du matériau personnalisé est absent"
    case "BS_FICHE_CONFIGUREE_ID_SHOULD_NOT_BE_NULL":
      return "L'id de la fiche configurée ne doit pas être null"
    case "BS_CUSTOM_MATERIAL_NOT_FOUND":
      return "Ce matériau personnalisé n'existe pas"
    case "PROJECT_USER_NOT_FOUND":
      return "Cet utilisateur n'existe pas sur ce projet"
    case "ORGANIZATION_MAX_BIM_MODEL_UPLOAD_COUNT_REACHED":
      return "Vous avez atteint le nombre maximum de maquettes autorisé"
    case "ERROR_BIM_MODEL_STREAM":
      return "Erreur lors du chargement du fichier. Veuillez ré-essayer ou appeler le support"
    case "BIM_MODEL_INVALID_JSON":
      return "Les données reçues pour le modèle BIM sont invalides"
    case "BS_BIM_MODEL_NOT_FOUND":
      return "La maquette BIM demandée n'existe pas"
    case "BAD_CHARACTER_IN_NAME":
      return "Ce zip n'a pas pu être lu comme un RSEE. S'il s'agit bien d'un RSEE, vérifiez les noms des fichiers et dossiers qu'il contient : ils ne doivent pas comporter de caractères spéciaux ou non pris en charge (accents, symboles)."
    case "CANT_DELETE_BIM_MODEL_AFFECTED_ON_VARIANT":
      return "La maquette est affectée à un calcul. Elle ne peut pas être supprimée"
    case "BIM_MODEL_BUILDING_NOT_FOUND":
      return "Le bâtiment de la maquette demandé n'existe pas"
    case "BIM_MODEL_BUILDING_NOT_IN_MODEL":
      return "Ce bâtiment n'appartient pas à la maquette sélectionnée"
    case "BIM_MODEL_BUILDING_REQUIRED":
      return "Cette maquette contient plusieurs bâtiments : sélectionnez le bâtiment à affecter"
    case "BIM_MODEL_BUILDING_AFFECTED_ON_VARIANT":
      return "Ce bâtiment de maquette est affecté à un calcul. Il ne peut pas être supprimé"
    case "SEND_EMAIL_ERROR":
      return "Une erreur s'est produite lors de l'envoi de l'email. Veuillez inviter de nouveau l'utilisateur"
    case "SURFACE_PARCELLE_MUST_BE_GREATER_THAN_ZERO":
      return "La surface parcelle doit être supérieure à zéro"
    case "BS_PARCELLE_NOT_FOUND":
      return "Les informations de la parcelle n'ont pas été trouvées"
    case "BS_PARCELLE_COMPONENT_NOT_FOUND":
      return "Le composant de la parcelle n'a pas été trouvé"
    case "BS_SURFACE_PARCELLE_EMPTY":
      return "La surface parcelle est obligatoire, mais elle est vide"
    case "BS_ENERGIE_NOT_FOUND":
      return "Les informations énergie n'ont pas été trouvées pour cette étude"
    case "BS_ENERGIE_INVALID_FILE":
      return "Le fichier RSET/RSEE est invalide ou ne contient pas de données exploitables"
    case "BS_ENERGIE_PARSING_ERROR":
      return "Une erreur est survenue lors de l'analyse du fichier RSET/RSEE"
    case "BS_ENERGIE_ZONE_MATCHING_INVALID":
      return "Les correspondances de zones ne sont pas valides. Veuillez vérifier que chaque zone RSET est associée à une zone existante de l'étude"
    case "BS_DEBUG_MODE_REQUIRED":
      return "L'initialisation d'une étude depuis un RSEE est réservée au mode debug de votre organisation"
    // US 2549 — the items the user adds in the advanced questionnaire
    case "BS_ITEM_NOT_CUSTOM":
      return "Seuls les items que vous avez ajoutés peuvent être modifiés ou supprimés ici"
    case "BS_CUSTOM_ITEM_NAME_EMPTY":
      return "Donnez un nom à l'item"
    case "BS_CUSTOM_ITEM_NAME_FIELD_TOO_LONG":
      return "Le nom de l'item est trop long"
    case "BS_CUSTOM_ITEM_SUB_CATEGORY_NOT_IN_CATEGORY":
      return "Cette sous-catégorie n'appartient pas à la catégorie choisie"
    case "BS_CUSTOM_ITEM_TYPE_NOT_IN_SUB_CATEGORY":
      return "Cet item ne peut pas être ajouté dans cette sous-catégorie"
    case "BS_CUSTOM_ITEM_FORFAIT_SUB_CATEGORY":
      return "Aucun item ne peut être ajouté dans un lot pouvant être au forfait (CVC, électricité)"
    case "BS_CUSTOM_ITEM_QUANTITY_INVALID":
      return "Les quantités d'un item doivent être des nombres positifs ou nuls"
    case "FIELD_CANNOT_BE_EMPTY":
      return "Le champ ne peut pas être vide"
    case "USER_HAS_NO_ORGANIZATION_ID":
      return "Votre utilisateur n'est pas lié à une organisation. Cela ne devrait pas être possible. Veuillez contacter le support"

    // Material lib
    case "MATERIAL_NOT_FOUND":
      return "Le matériel n'existe pas dans l'organisation de l'utilisateur"
    case "GAMME_FONCTION_NOT_FOUND":
      return "Cette fonction de gamme n'existe pas"
    case "LIBRARY_REQUEST_INVALID":
      return "Cette demande sur la bibliothèque des composants ne peut pas être enregistrée"
    case "FICHE_CONFIGUREE_NOT_FOUND":
      return "La fiche configurée sélectionnée n'existe plus dans l'organisation"
    case "AT_LEAST_ONE_AMONG_INIES_ID_AND_FICHE_CONFIGUREE_ID":
      return "Il faut renseigner un inies ID ou un ID de fiche configurée"

    // Beem Pilot
    case "RSEE_RESULT_NOT_FOUND":
      return "Impossible de mettre à jour le résultat pour cette zone de ce bâtiment. Il n'existe pas"
    case "ERROR_COMPARE_OVERRIDDEN_TO_ORIGINAL":
      return "Erreur technique lors de la réactivation d'une fiche"
    case "RSEE_COMENV_RESULT_NOT_FOUND":
      return "Aucun résultat de calcul n'est encore disponible pour ce RSEE. Veuillez lancer le calcul."
    case "RSEE_COMENV_XML_DOWNLOAD_FAILED":
      return "Le fichier RSEE n'a pas pu être récupéré ou lu. Veuillez ré-essayer ou contacter le support"
    case "RSEE_DOCUMENT_HAS_NO_FILE_KEY":
      return "Aucun fichier n'est associé à ce RSEE. Veuillez le ré-importer"
    case "REPORT_XML_NOT_FOUND":
      return "Le fichier XML du RSEE est introuvable, le rapport ne peut pas être généré"
    case "REPORT_GENERATION_FAILED":
      return "La génération du rapport RE2020 environnement a échoué. Veuillez ré-essayer"

    // Beem Shot
    // BSItem
    case "BS_ITEM_CANNOT_BE_RESET":
      return "Cet item ne peut pas être reset. Il n'est pas surchargé"
    // US 2589 — the materiality of an item copied to items of the same nature
    case "BS_ITEM_MATERIALITY_CANNOT_BE_COPIED":
      return "La matérialité d'un item ignoré ou en forfaitaire ne peut pas être copiée"
    case "BS_ITEM_CANNOT_TAKE_MATERIALITY":
      return "Un des items cochés ne peut plus recevoir cette matérialité (ignoré, en forfaitaire ou supprimé entre-temps) : rouvrez la liste"
    case "REPARTITION_SHOULD_BE_100":
      return "Le total doit être égal à 100"
    case "ITEM_WRONG_BUILDING":
      return "Un des items n'appartient pas au bâtiment"
    case "IMPOSSIBLE_TO_ALLOCATE_NOT_EXTRAIT":
      return "L'un des items n'a pas de code extrait de la maquette. Seuls les items issus de la maquette peuvent être répartis"
    case "INCORRECT_CODE_OCCURRENCE":
      return "L'un des items n'a pas le bon code+occurrence"
    case "BS_ITEM_SHOULD_BE_SAME":
      return "La liste ne contient pas le bon nombre d'item à répartir"
    case "BS_ITEM_HAS_NO_REPARTITION":
      return "Il manque la répartition sur une des zones"
    case "REPARTITION_AND_ITEM_ZONE_SHOULD_BE_SAME":
      return "Veuillez contacter le support. L'une des zones modifiées ne correspond pas à l'item"

    case "BS_MATERIAL_NOT_FOUND":
      return "Le composant à personnaliser est introuvable. Veuillez recharger la page puis réessayer."

    // BS Building
    case "BS_BUILDING_NOT_FOUND":
      return "Le bâtiment demandé n'existe pas"
    case "TOTAL_SURFACE_MUST_BE_POSITIVE":
      return "La surface du bâtiment ne peut être inférieure à 1"

    // BS Chantier
    case "BS_CHANTIER_COMPONENT_NOT_FOUND":
      return "Le composant du chantier n'a pas été trouvé"

    case "ESTIMATION_NO_MORE_LICENCE":
      return "Vous avez atteint le maximum de projet avec une étude pour votre organisation. Veuillez nous contacter pour augmenter votre maximum"
    case "ORGANIZATION_IS_BLOCKED":
      return getOrganizationBlockedMessage("")
    case "USER_IS_BLOCKED":
      return "Votre accès à Time To Beem a été bloqué. Contactez l'administrateur de votre organisation ou Time To Beem."
    case "COMMERCIAL_OFFER_NOT_FOUND":
      return "Cette offre commerciale n'existe plus"
    case "COMMERCIAL_OFFER_NAME_INVALID":
      return "Le nom de l'offre doit contenir entre 1 et 200 caractères"
    case "COMMERCIAL_OFFER_DATES_INVALID":
      return "L'offre doit avoir une date de début et une date de fin, la fin ne pouvant précéder le début"
    case "COMMERCIAL_OFFER_QUOTA_INVALID":
      return "Un quota de l'offre est invalide : saisissez un nombre positif ou « Illimité »"
    case "COMMERCIAL_OFFER_CLOSED":
      return "Cette offre est clôturée : elle ne peut plus être modifiée ni activée"
    case "COMMERCIAL_OFFER_NOT_CLOSED":
      return "Cette offre n'est pas clôturée : il n'y a rien à rétablir"
    case "CONTACT_NOT_FOUND":
      return "Ce contact n'existe plus"
    case "CONTACT_NAME_REQUIRED":
      return "Le prénom et le nom du contact sont requis"
    case "CONTACT_EMAIL_INVALID":
      return "L'adresse email du contact est invalide"
    case "CONTACT_ALREADY_EXISTS":
      return "Un contact de cette organisation a déjà cette adresse email"
    case "CONTACT_IS_ALREADY_A_USER":
      return "Cette personne a déjà un compte Time To Beem : elle ne peut pas être enregistrée comme simple contact"
    case "CONTACT_PHONE_REQUIRED":
      return "Un numéro de téléphone est requis pour inviter ce contact"
    case "CONTACT_ROLE_INVALID":
      return "Ce rôle ne peut pas être attribué à un contact"
    case "L_DIMENSION_1_UNDER_DIMENSION_3":
      return "La dimension 1 doit être supérieure à la dimension 3."
    case "L_DIMENSION_2_UNDER_DIMENSION_4":
      return "La dimension 2 doit être supérieure à la dimension 4."
    case "MANUAL_OUTLINE_NOT_CLOSED":
      return "Le contour saisi doit être fermé avant d'enregistrer."
    case "MANUAL_OUTLINE_INSUFFICIENT_VERTICES":
      return "Le contour saisi doit comporter au moins 3 sommets."
    case "MANUAL_OUTLINE_SELF_INTERSECTING":
      return "Le contour saisi se croise : ajustez une longueur ou un angle."
    case "MORPHOLOGY_PLAN_NOT_FOUND":
      return "Aucun plan importé n'est enregistré pour ce bâtiment."
    case "MORPHOLOGY_PLAN_SOURCE_NOT_FOUND":
      return "Le fichier PDF source de ce plan est introuvable."
    case "MORPHOLOGY_PLAN_SOURCE_INVALID":
      return "Le fichier source du plan n'est pas un PDF valide."
    case "MORPHOLOGY_PLAN_SOURCE_HASH_MISMATCH":
      return "Le fichier PDF source reçu ne correspond pas à son empreinte."
    // « Lire contour avec IA » (US 2550)
    case "PLAN_READING_OPTION_REQUIRED":
      return "La lecture des plans n'est pas incluse dans l'offre de votre organisation."
    case "PLAN_AI_READING_NOT_FOUND":
      return "Cette lecture du plan par l'IA est introuvable."
    case "PLAN_AI_READING_IMAGE_INVALID":
      return "La page du plan envoyée à l'IA n'est pas une image lisible."
    case "PLAN_AI_READING_ANSWER_INVALID":
      return "La réponse de l'IA n'était pas exploitable. Réessayez"
    case "PLAN_AI_READING_FAILED":
      return "La lecture du plan par l'IA a échoué. Réessayez"

    // BS Level
    case "BS_BUILDING_LEVEL_LIST_MUST_NOT_BE_EMPTY":
      return "Vous devez créer des niveaux afin de poursuivre"
    case "BS_BUILDING_LEVEL_LIST_MUST_BE_UNDER_200":
      return "Vous ne pouvez pas créer plus de 200 niveaux"
    case "BS_BUILDING_LEVEL_GROUP_SHAPES_DIFFER":
      return "Les niveaux d'un même groupe n'ont pas tous la même forme : rechargez la page et réessayez"
    // US 2574 — the level group closest to the ground carries the shape of the building
    case "BS_BUILDING_GROUND_LEVEL_HAS_NO_OUTLINE":
      return "La forme du bâtiment est celle de son rez-de-chaussée : dessinez son contour avant d'enregistrer les niveaux"
    // US 2378 — vertical circulations
    case "BS_VERTICAL_CIRCULATION_ZONE_NOT_ON_BUILDING":
      return "Une circulation verticale référence une zone qui n'appartient pas à ce bâtiment : rechargez la page et réessayez"
    case "BS_VERTICAL_CIRCULATION_NOT_ON_BUILDING":
      return "Une circulation verticale référence un identifiant qui n'appartient pas à ce bâtiment : rechargez la page et réessayez"

    // BS Parking
    case "BS_PARKING_NOT_FOUND":
      return "Le parking demandé n'existe pas"
    case "BIM_PARKING_NOT_DELETABLE":
      return "Un parking modélisé ne peut pas être supprimé"
    case "PARKING_MUST_HAVE_ID":
      return "L'identifiant du parking doit être présent"
    case "PARKING_TYPE_ALREADY_EXISTS":
      return "Ce bâtiment BIM contient déjà un parking de ce type"
    case "SOUTERRAIN_NOT_ALLOWED_FOR_BIM_BUILDING":
      return "Un parking souterrain ne peut pas être créé pour un bâtiment BIM"
    case "SUPER_STRUCTURE_NOT_ALLOWED_FOR_BIM_BUILDING":
      return "Un parking en superstructure ne peut pas être créé pour un bâtiment BIM"

    // BS Patio
    case "BS_PATIO_IMPACTED_FLOOR_TOO_HIGH":
      return "Le nombre d'étages impactés doit être compris entre 1 et le nombre de niveaux restants par rapport au niveau de départ"
    case "BS_PATIO_NOT_FOUND":
      return "Le patio/vide demandé n'existe pas"
    case "BS_PATIO_ID_MUST_NOT_BE_NULL":
      return "L'id du patio/vide doit être présent"

    // BS Zone
    case "BS_ZONE_NOT_FOUND":
      return "La zone demandée n'existe pas"
    case "FORBIDDEN_TO_DELETE_ASSIGNED_ZONE":
      return "La zone est affectée sur un niveau ou un parking donc on ne peut pas la supprimer"
    case "BS_ZONE_USAGE_LOCKED_BY_RSET":
      return "Cette zone est rattachée au Rset (partie énergie) et il est impossible de changer son usage."
    case "SURFACE_ZONE_MUST_BE_POSITIVE":
      return "La surface de la zone ne peut être inférieure à 1"
    case "SURFACE_PLANCHER_ZONE_MUST_BE_POSITIVE":
      return "La surface plancher de la zone ne peut être inférieure à 1"
    case "BS_BUILDING_HAS_NO_LEVEL":
      return "Aucun niveau n'est défini sur le bâtiment. Définissez les niveaux avant de lancer le calcul."

    // BS bim model
    case "AVERAGE_LENGTH_CAN_NOT_BE_ZERO":
      return "La longueur moyenne d'un bâtiment ne peut pas être égale à 0. Veuillez mettre à jour les informations de la maquette."

    // Mlab
    case "MLAB_DEMANDEUR_CREATION_FAILED":
      return "Erreur lors de la création du demandeur Mlab"
    case "MLAB_DEMANDE_CREATION_FAILED":
      return "Erreur lors de la création de la demande DED"
    case "MLAB_DEMANDE_CONFLICT":
      return "Conflit lors de la création de la demande DED. Une demande similaire existe peut-être déjà"
    case "MLAB_MISSING_REQUIRED_FIELDS":
      return "Des champs obligatoires sont manquants pour la demande Mlab"
    case "MLAB_DEMANDE_NOT_FOUND":
      return "Demande Mlab introuvable pour cet utilisateur"
    case "MLAB_API_UNAVAILABLE":
      return "Le service Mlab est temporairement indisponible. Veuillez réessayer ultérieurement"

    // RIGHTS
    case "FORBIDDEN_ORGANIZATION_USER":
      return "Ce projet n'appartient pas à votre organisation."

    // BS Project
    case "PROJECT_ARCHIVED":
      return "Vous ne pouvez pas modifier un projet archivé."

    // RSEE Generation
    case "CERTIFICATION_RESULT_RECALCULATION_IN_PROGRESS":
      return "Le calcul de certification est en cours, les résultats seront disponibles dans quelques instants."
    case "CERTIFICATION_VARIANT_TYPE_INVALID":
      return "La génération de RSEE n'est possible que sur les études de type Certification"
    case "CERTIFICATION_NO_RSET_FILE":
      return "Aucun fichier RSET n'a été uploadé sur cette étude. Veuillez importer un fichier RSET dans l'onglet Énergie"
    case "CERTIFICATION_RSET_FILE_NOT_VALIDATED":
      return "Les correspondances de zones du fichier RSET n'ont pas été validées"
    case "CERTIFICATION_OPERATION_CONFLICT":
      return (
        "Le fichier RSET comporte des points critiques : conflit avec les données du projet (zone climatique, altitude ou date de dépôt PC)" +
        " ou données refusées par le moteur réglementaire (coordonnées GPS absentes, données techniques non renseignées)." +
        " Consultez le diagnostic du fichier dans l'onglet Énergie pour les corriger."
      )
    case "CERTIFICATION_MISSING_DATASCOMP_XML":
      return "Le fichier RSET ne contient pas de section DatasComp exploitable"
    case "CERTIFICATION_MISSING_RSET_XML":
      return "Le fichier RSET ne contient pas de section RSET exploitable"
    case "CERTIFICATION_RSET_MALFORMED_NUMERIC_VALUE":
      return "Le fichier RSET contient une valeur numérique mal formée (retour à la ligne au milieu d'un nombre). Le fichier doit être ré-exporté ou corrigé. Consultez les logs pour identifier le champ concerné"
    case "CERTIFICATION_INSUFFICIENT_NUMBERED_LOTS":
      return "Au moins 9 des 13 lots numérotés doivent être renseignés pour générer le RSEE"
    case "IMPORT_FICHE_CONFIGUREE_TO_CSTB_FAILED":
      return "L'une des fiches configurées est invalide. Si vous ne trouvez pas laquelle, contactez-nous"
    case "CERTIFICATION_RSEE_BLOCKED_BY_MATERIALS_IN_ERROR":
      return "Impossible de générer le RSEE : des matériaux ont été exclus du calcul (fiche refusée par le moteur réglementaire). Corrigez ou remplacez les matériaux en erreur, puis relancez le calcul"
    case "CERTIFICATION_MISSING_FICHE_CONFIGUREE_XML":
      return "Nous n'avons pas sauvegardé les informations d'une fiche configurée. Il faut la ré-importer. Si vous ne trouvez pas laquelle, contactez-nous"
    case "CERTIFICATION_JOB_FAILED":
      return "Le calcul a échoué. Réessayez dans quelques instants, et contactez-nous si le problème persiste"
    case "CERTIFICATION_JOB_STUCK":
      return "Le calcul n'a pas pu aboutir après plusieurs tentatives. Relancez-le, et contactez-nous si le problème persiste"
    case "CERTIFICATION_CSTB_COMENV_AUTH_FAILED":
      return "L'authentification auprès du moteur réglementaire a échoué"
    case "CERTIFICATION_CSTB_COMENV_CORE_OBJECT_GENERATION_FAILED":
      return "Le moteur réglementaire a eu une erreur lors de la génération des données pour le RSEE"
    case "CERTIFICATION_CSTB_COMENV_CORE_OBJECT_EMPTY_BODY":
      return "Le moteur réglementaire a échoué à générer les données pour le RSEE"
    case "CERTIFICATION_CSTB_COMENV_RSEE_GENERATION_FAILED":
      return "La génération du RSEE par le moteur réglementaire a échoué"
    case "CERTIFICATION_CSTB_COMENV_RSEE_GENERATION_ERROR":
      return "Une erreur s'est produite lors de la génération du RSEE par le moteur réglementaire"
    case "CERTIFICATION_CSTB_COMENV_FICHE_CONFIGUREE_IMPORT_FAILED":
      return "L'import des fiches configurées dans le moteur de calcul a échoué. Si vous ne trouvez pas laquelle, contactez-nous"
    case "CERTIFICATION_CSTB_COMENV_FICHE_CONFIGUREE_IMPORT_ERROR":
      return "Une erreur s'est produite lors de l'import des fiches configurées dans le moteur de calcul. Si vous ne trouvez pas laquelle, contactez-nous"

    // Certification carbon calculation
    case "CERTIFICATION_CSTB_COMENV_CALCUL_FAILED":
      return "Le calcul carbone de certification par le moteur réglementaire a échoué"
    case "CERTIFICATION_IC_CONSTRUCTION_SEUIL_MISSING":
      return (
        "Aucun seuil IC construction réglementaire n'existe pour la date de dépôt du permis de construire renseignée. " +
        "Les usages secondaires (hôtellerie, restauration, commerces…) ne sont réglementés qu'à partir de 2025 : " +
        "renseignez une date de dépôt de permis à partir du 01/01/2025 dans les paramètres du projet."
      )
    case "CERTIFICATION_CSTB_COMENV_CALCUL_EMPTY_BODY":
      return "Le moteur réglementaire n'a renvoyé aucun résultat pour le calcul carbone de certification"
    case "CERTIFICATION_CORE_OBJECT_RESULT_NOT_FOUND":
      return "Aucun résultat de calcul carbone de certification n'est disponible pour cette étude"
    case "CERTIFICATION_BATIMENT_RESULT_NOT_FOUND":
      return "Aucun résultat de calcul carbone de certification n'est disponible pour ce bâtiment"
    case "CERTIFICATION_PARCELLE_RESULT_NOT_FOUND":
      return "Aucun résultat de parcelle n'est disponible pour cette étude"
    case "CERTIFICATION_RSEE_NOT_READY":
      return "Le RSEE n'est pas encore généré. Veuillez patienter quelques instants puis réessayer"

    // DPGF to Carbon (US 2597)
    case "DPGF_IMPORT_NOT_FOUND":
      return "Ce DPGF n'existe pas ou a été supprimé"
    case "DPGF_FILE_MISSING":
      return "Aucun fichier n'a été reçu"
    case "DPGF_FILE_NOT_A_SPREADSHEET":
      return "Ce fichier n'est pas un classeur Excel (.xlsx ou .xls)"
    case "DPGF_FILE_TOO_LARGE":
      return "Ce fichier est trop volumineux (25 Mo maximum)"
    case "DPGF_NO_LINE_READ":
      return "Aucune ligne n'a pu être lue dans ce classeur. Vérifiez la feuille et la ligne d'en-tête choisies"
    case "DPGF_TOO_MANY_LINES":
      return "Ce classeur contient trop de lignes (20 000 maximum). Vérifiez la feuille choisie"
    case "DPGF_IMPORT_NAME_TOO_LONG":
      return "Le nom du DPGF est trop long (500 caractères maximum)"
    case "DPGF_IMPORT_LOT_LABEL_TOO_LONG":
      return "Le nom du lot est trop long (500 caractères maximum)"
    case "DPGF_IMPORT_COMPANY_NAME_TOO_LONG":
      return "Le nom de l'entreprise est trop long (500 caractères maximum)"
    case "DPGF_PAIRING_TOO_MANY_FILES":
      return "Trop de fichiers à associer en une fois (100 DPGF et 100 CCTP au plus). Déposez le dossier en plusieurs fois"
    case "DPGF_LINE_NOT_FOUND":
      return "Cette ligne n'existe pas ou a été supprimée"
    case "DPGF_CLASSIFICATION_ALREADY_RUNNING":
      // The code was named in J2, when classification was the only step; it now guards the fiche
      // step too, so the sentence covers both rather than naming the wrong one.
      return "Un traitement est déjà en cours sur ce DPGF"
    // The provider's own failures, shared by every module that calls a model (US 2532).
    case "LLM_CALL_FAILED":
      return "L'IA n'a pas répondu. Réessayez dans quelques instants"
    case "LLM_QUOTA_REACHED":
      return "Le budget alloué à l'IA est atteint. Contactez le support"
    case "LLM_EMPTY_ANSWER":
      return "L'IA a répondu sans contenu. Réessayez"
    case "DPGF_LLM_ANSWER_UNUSABLE":
      return "La réponse de l'IA n'était pas exploitable. Réessayez"
    case "DPGF_CCTP_NOT_FOUND":
      return "Ce CCTP n'existe pas ou a été supprimé"
    case "DPGF_CCTP_FILE_MISSING":
      return "Aucun fichier CCTP n'a été reçu"
    case "DPGF_CCTP_NOT_A_DOCUMENT":
      return "Un CCTP doit être un fichier PDF ou Word (.docx)"
    case "DPGF_CCTP_FILE_TOO_LARGE":
      return "Ce CCTP est trop volumineux (50 Mo maximum)"
    case "DPGF_CCTP_NO_TEXT_READ":
      return "Aucun texte n'a pu être lu dans ce CCTP, même après reconnaissance de caractères"
    case "DPGF_CCTP_REQUIRED":
      return "Ajoutez au moins un CCTP : c'est lui qui porte les références produits"
    case "DPGF_NOT_CLASSIFIED_YET":
      return "Classez d'abord les lignes du DPGF : les fiches s'affectent sur des lignes classées"
    case "DPGF_NO_CLASSIFIED_LINE":
      return "Aucune ligne classée : le classement les a toutes écartées. Un lot sans matière incorporée au bâtiment, comme une démolition, n'a pas de fiche à affecter"
    case "DPGF_PRODUCT_NOT_FOUND":
      return "Ce produit n'existe pas ou a été supprimé"
    case "DPGF_FICHE_NOT_FOUND":
      return "Cette fiche INIES n'existe pas, n'est plus active, ou n'a pas été retenue pour ce lot"
    case "DPGF_CARBON_NO_LINE_TO_SEND":
      return "Aucune ligne à calculer : il faut au moins une ligne classée, avec une fiche et une quantité"
    case "DPGF_CARBON_RESULT_NOT_FOUND":
      return "Le calcul carbone de ce projet n'a pas encore été lancé"

    // Shared by every module that calls the LLM (US 2597)
    case "LLM_ORGANIZATION_BUDGET_REACHED":
      return "Le budget IA est épuisé. Une étape déjà lancée va à son terme, mais aucune nouvelle ne démarre : contactez votre administrateur"
    case "LLM_BUDGET_EXCEEDS_PARENT":
      return "Ce budget dépasse ce qui reste au niveau supérieur. Une enveloppe se partage : augmentez d'abord la région ou l'organisation, ou baissez une autre agence"
    case "LLM_BUDGET_OVER_HARD_LIMIT":
      return "Le budget IA d'une organisation est limité à 100 $ par module. Contactez le support pour aller au-delà"
    case "LLM_BUDGET_TARGET_NOT_FOUND":
      return "Cette région ou cette agence n'existe pas, ou n'appartient pas à votre organisation"
    case "LLM_MODULE_OPTION_REQUIRED":
      return "Ce module IA n'est pas inclus dans l'offre de votre organisation"
    // US 2607 — the simulated runs of debug mode
    case "LLM_MOCK_REQUIRES_DEBUG_MODE":
      return "L'IA simulée est réservée au mode debug de l'organisation du projet"
    // Greta — audit de conformité des CCTP (US 2532)
    case "GRETA_AUDIT_NOT_FOUND":
      return "Cet audit n'existe pas ou a été supprimé"
    case "GRETA_FILE_MISSING":
      return "Aucun fichier n'a été reçu"
    case "GRETA_FILE_TOO_LARGE":
      return "Ce fichier est trop volumineux (25 Mo maximum)"
    case "GRETA_FILE_NOT_A_SPREADSHEET":
      return "Ce fichier doit être un tableur : .csv, .txt, .slk, .xlsx ou .xls"
    case "GRETA_NO_PRESCRIPTION_READ":
      return "Aucune prescription n'a pu être lue. Vérifiez la feuille et la ligne d'en-tête choisies"
    case "GRETA_TOO_MANY_PRESCRIPTIONS":
      return "Ce fichier contient trop de prescriptions (20 000 maximum)"
    case "GRETA_CCTP_NOT_FOUND":
      return "Ce CCTP n'existe pas ou a été supprimé"
    case "GRETA_CCTP_FILE_MISSING":
      return "Aucun CCTP n'a été reçu"
    case "GRETA_CCTP_NOT_A_DOCUMENT":
      return "Un CCTP doit être un fichier PDF ou Word (.docx)"
    case "GRETA_CCTP_FILE_TOO_LARGE":
      return "Ce CCTP est trop volumineux (50 Mo maximum)"
    case "GRETA_LINE_NOT_FOUND":
      return "Cette ligne d'audit n'existe plus"
    case "GRETA_FICHE_NOT_FOUND":
      return "Aucune fiche INIES active ne porte ce numéro"
    case "GRETA_AUDIT_FAILED":
      return "L'audit s'est arrêté sur une erreur. Relancez-le"
    case "GRETA_CCTP_REQUIRED":
      return "Déposez au moins un CCTP avant de lancer l'audit"
    case "GRETA_AUDIT_ALREADY_RUNNING":
      return "Un audit est déjà en cours sur ce dossier"
    case "GRETA_LLM_ANSWER_UNUSABLE":
      return "La réponse de l'IA n'était pas exploitable. Relancez l'audit"
    case "GRETA_CCTP_NO_TEXT_READ":
      return "Aucun texte n'a pu être lu dans ce CCTP. Il est probablement scanné : il faut une version texte"

    // Quanti (US 2886)
    case "QUANTI_VARIANT_NOT_FOUND":
      return "Cette variante Quanti n'existe pas ou a été supprimée"
    case "QUANTI_BUILDING_NOT_FOUND":
      return "Ce bâtiment n'existe pas ou a été supprimé"
    case "QUANTI_BUILDING_ANNEX_INVALID":
      return (
        "Le rattachement de l'annexe est incohérent : une annexe se rattache à un ou plusieurs bâtiments de la variante " +
        "(jamais à elle-même ni à une autre annexe), et les pourcentages font 100 % au total"
      )
    case "QUANTI_VARIANT_NAME_FIELD_TOO_LONG":
      return "Le nom de la variante est trop long (500 caractères maximum)"
    case "QUANTI_VARIANT_DESCRIPTION_FIELD_TOO_LONG":
      return "La description de la variante est trop longue (1000 caractères maximum)"
    case "QUANTI_DOCUMENT_NOT_FOUND":
      return "Ce document n'existe pas ou a été supprimé de la bibliothèque"
    case "QUANTI_DOCUMENT_WRONG_VARIANT":
      return "Ce document n'appartient pas à cette variante"
    case "QUANTI_DOCUMENT_GEOREFERENCE_INVALID":
      return "Le géoréférencement du plan est incomplet : recalez-le sur le terrain avant de l'enregistrer"
    case "QUANTI_DOCUMENT_FILE_MISSING":
      return "Aucun fichier PDF n'a été reçu"
    case "QUANTI_DOCUMENT_NOT_A_PDF":
      return "Ce fichier n'est pas un PDF"
    case "QUANTI_DOCUMENT_FILE_TOO_LARGE":
      return "Ce PDF dépasse 50 Mo : il ne peut pas être ajouté à la bibliothèque"
    case "QUANTI_DOCUMENT_UNREADABLE":
      return "Ce PDF n'a pas pu être lu : il est peut-être corrompu ou protégé par mot de passe"
    case "QUANTI_DOCUMENT_PAGE_SELECTION_INVALID":
      return "La sélection de pages est invalide : cochez au moins une page du document"
    case "QUANTI_DOCUMENT_SCALE_INVALID":
      return "L'échelle saisie est invalide : elle doit être un nombre strictement positif"
    case "QUANTI_DOCUMENT_CALIBRATED_PAGE_OUT_OF_RANGE":
      return "La page à calibrer n'existe pas dans ce document"
    case "QUANTI_DOCUMENT_NOT_CALIBRATED":
      return "Ce plan n'a pas encore d'échelle : donnez son échelle avant de l'utiliser"
    case "QUANTI_DOCUMENT_FILE_NAME_TOO_LONG":
      return "Le nom du document est trop long (500 caractères maximum)"
    case "QUANTI_DOCUMENT_FILE_NOT_FOUND_IN_STORAGE":
      return "Le fichier de ce document est introuvable dans le stockage. Veuillez contacter le support"
    case "QUANTI_TERRAIN_CADASTRAL_REFERENCE_INVALID":
      return "La référence cadastrale est invalide : code commune INSEE à 5 caractères, section de 1 ou 2 lettres, numéro de 1 à 4 chiffres"
    case "QUANTI_TERRAIN_COORDINATES_INVALID":
      return "Les coordonnées du terrain sont invalides : latitude et longitude vont ensemble, en degrés décimaux"
    case "QUANTI_TERRAIN_ALTITUDE_INVALID":
      return "L'altitude du terrain est invalide : entre -500 et 5 000 m NGF"
    case "QUANTI_TERRAIN_ADDRESS_NOT_FOUND":
      return "Cette adresse n'a pas été reconnue : précisez le numéro, la rue, le code postal et la commune"
    case "QUANTI_TERRAIN_LOOKUP_UNAVAILABLE":
      return "Le service de l'IGN ou de la Base Adresse Nationale n'a pas répondu : il limite parfois le nombre d'appels. Réessayez dans quelques instants"
    case "QUANTI_EARTHWORK_INVALID":
      return "La part du déblai réutilisée en remblai doit être comprise entre 0 et 100 %"
    case "QUANTI_BUILDING_EARTHWORK_INVALID":
      return "Le débord de plateforme et la surlargeur de fouille doivent être compris entre 0 et 100 m"
    case "QUANTI_TERRAIN_RELIEF_POINTS_INVALID":
      return "Le relief ne peut pas être échantillonné : la parcelle est trop petite ou trop grande pour une grille"
    case "QUANTI_OPERATION_COUPES_INVALID":
      return "Les coupes de l'opération sont incohérentes : chaque coupe doit avoir un nom, et le plan rattaché doit être calibré"
    case "QUANTI_OPERATION_FACADES_INVALID":
      return "Les façades de l'opération sont incohérentes : chaque façade doit avoir un nom, et le plan rattaché doit être calibré"
    case "QUANTI_OPERATION_LEVELS_INVALID":
      return "Les niveaux de l'opération sont incohérents : un plan est rattaché à un niveau qui n'existe pas, ou deux fois au même niveau"
    case "QUANTI_SHEET_PLACEMENT_INVALID":
      return (
        "Le placement d'une feuille sur une autre est incohérent : les deux feuilles doivent être distinctes et calibrées, " +
        "le point de repère donné sur chacune, une feuille ne peut pas reposer sur une feuille qui repose sur elle, ni être le morceau d'un morceau"
      )
    case "QUANTI_BUILDING_COMMON_WALLS_INVALID":
      return (
        "Le choix du mur commun entre deux bâtiments n'a pas été enregistré : il doit relier deux bâtiments différents " +
        "de la variante, un niveau et un contour de chacun, une seule fois par paire"
      )

    default:
      return message
  }
}
